import { createEffect, onCleanup } from 'solid-js';

// Focus containment for role="dialog" surfaces (the `testing/overlay-shells.ts`
// list: Modal, ConfirmDialog, ActionDrawer, CommandPaletteDialog, BottomSheet
// and the open AppSidebar nav drawer): initial focus, Tab trap, Escape to close,
// and focus restore to the trigger on close. One implementation — a dialog that
// traps differently from the other is a bug.
//
// Dialogs STACK: the palette (⌘K) and the `?` sheet open over whatever dialog is
// already up, and a confirm can open from inside a modal. Every open trap binds
// its own document listener, so without an owner all of them answered every key
// — one Escape closed the whole stack, and the bottom trap "recaptured" a Tab
// that was inside the top dialog, dragging focus into the one underneath. The
// stack below makes the most recently opened trap the only one that acts; the
// rest wait until it closes.
//
// Popover (#5205 review, round 3) is not a modal trap — it never calls
// `useDialogTrap` — but it DOES need to know whether it is the topmost
// overlay before it may consume Escape: a dialog opened OVER an already-open
// Popover (the ⌘K palette, the `?` sheet, or a ConfirmDialog opened from a
// panel item — none of which click anything) must own the first Escape, not
// the hidden Popover underneath. `pushLayer`/`popLayer`/`isTopLayer` are the
// same ownership stack a modal trap pushes itself onto, exposed so a
// non-modal layer can register on it too without becoming a focus trap
// itself (no Tab handling, no initial-focus, no restore-to-trigger here —
// that stays this module's business alone).
//
// Escape ownership and Tab ownership are NOT the same question, which is why
// each layer carries a `modal` flag instead of the stack staying a plain
// list of tokens. Escape goes to the topmost layer of EITHER kind — a
// Popover above a Modal wins it, same as two stacked Modals. Tab containment
// is a modal-only concern: `isTopModal` skips over any non-modal (Popover)
// layers to find the nearest modal, so a Popover opening on top of an
// ActionDrawer must not switch off the drawer's Tab trap just by sitting
// above it on the shared stack — the Popover was never a focus trap, and
// nothing else is holding Tab in the meantime. When every layer on the stack
// is modal (the `OVERLAY_SHELLS`, nested or not), `isTopModal` and
// `isTopLayer` agree on every token, so this is byte-identical to the old
// single-stack behaviour for them.

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]';

/**
 * A modal layer carries its dialog element (the trap's `el` ref), so a closing
 * dialog whose opener is gone can hand focus to the dialog still open beneath
 * it ({@link restore}). A non-modal layer (Popover) has none: it never takes
 * focus back.
 */
type Layer =
  | { readonly token: symbol; readonly modal: false }
  | { readonly token: symbol; readonly modal: true; readonly el: () => HTMLElement | undefined };

/** Open traps AND non-modal layers (e.g. Popover), oldest first; only the last one owns Escape. */
const stack: Layer[] = [];

/** Register a non-modal layer (e.g. Popover) on the shared overlay stack; returns its token. */
export function pushLayer(): symbol {
  const token = Symbol('overlay-layer');
  stack.push({ token, modal: false });
  return token;
}

/** Register a modal layer (a `useDialogTrap` caller) on the shared overlay stack; returns its token. */
function pushModalLayer(el: () => HTMLElement | undefined): symbol {
  const token = Symbol('dialog-trap');
  stack.push({ token, modal: true, el });
  return token;
}

/** The dialog element of the most-recently-opened modal layer still on the stack. */
function topModalElement(): HTMLElement | undefined {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const layer = stack[i];
    if (layer?.modal === true) return layer.el();
  }
  return undefined;
}

/** Remove a layer from the stack, wherever it sits (defensive against out-of-order cleanup). */
export function popLayer(token: symbol): void {
  const idx = stack.findIndex((layer) => layer.token === token);
  if (idx !== -1) stack.splice(idx, 1);
}

/** Whether `token` is the most-recently-opened layer of any kind — the one that owns Escape. */
export function isTopLayer(token: symbol): boolean {
  return stack.at(-1)?.token === token;
}

/** Whether `token` is the most-recently-opened MODAL layer — the one that owns Tab containment. */
function isTopModal(token: symbol): boolean {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const layer = stack[i];
    if (layer?.modal === true) return layer.token === token;
  }
  return false;
}

/**
 * The controls Tab can actually land on, in DOM order. The selector alone is
 * not that set: a roving-tabindex rail parks its inactive tabs at
 * `tabindex="-1"`, and `<Tabs/>` keeps every panel mounted under `hidden`. A
 * cycle whose `last` is a button in a hidden panel never wraps — the browser
 * skips it and Tab walks out of the dialog into the page.
 */
function reachable(dialog: HTMLElement): HTMLElement[] {
  return [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (item) => item.tabIndex >= 0 && item.closest('[hidden],[inert]') === null,
  );
}

/** Wrap Tab at either end of the dialog, and pull a focus that escaped back in. */
function keepTabInside(dialog: HTMLElement, event: KeyboardEvent): void {
  const items = reachable(dialog);
  const first = items[0];
  const last = items[items.length - 1];
  if (first === undefined || last === undefined) return;
  // Recapture: a backdrop click (or a control removed/disabled mid-flight)
  // drops focus to <body>; the next Tab must land back inside the dialog
  // rather than walking the page underneath.
  //
  // This said "the aria-hidden page underneath", and NOTHING in this app
  // hides it: every `aria-hidden` under `src/` is on a decorative span or
  // icon, and no `inert` is set anywhere. What confines a screen reader is
  // `aria-modal="true"`, which every shell carries while open — the set
  // written down in `testing/overlay-shells.ts` and held equal to both the dialog-role
  // scan and this hook's call sites by `overlay-shells.test.ts`.
  //
  // The distinction is load-bearing, which is why the sentence was worth
  // fixing rather than leaving: `aria-modal` steers the VIRTUAL cursor and
  // does nothing to Tab, so this recapture is the only thing keeping
  // keyboard focus in. A reader who believed the page was hidden would
  // read the next four lines as redundant and delete them.
  if (!dialog.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }
  const edge = event.shiftKey ? first : last;
  if (document.activeElement === edge) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

/**
 * Hand focus back on close (call AFTER this layer is popped), in order:
 *
 *  1. the control that had it at open, if it is still in the document;
 *  2. else the dialog still open beneath — the topmost remaining modal layer:
 *     its first reachable control, or the dialog itself. A confirm opened from
 *     a row inside an ActionDrawer or Modal that deletes the row must leave the
 *     reader in that drawer, never on the page behind its `aria-modal`;
 *  3. else, with no dialog left, the page's `<main id="main" tabindex="-1">`
 *     (AppShell, the skip link's target). Outside the shell (no `#main`) focus
 *     stays put.
 *
 * Cases 2 and 3 are the ordinary path for a destructive confirm:
 * `createConfirmGate` awaits the act and the act awaits its re-read, so the row
 * whose button opened the confirm is already unmounted when the confirm closes.
 * Focusing a detached node is a no-op, and focus then fell to `<body>` once the
 * dialog unmounted (plan 101 slice 19, 19-S2), restarting the Tab order at the
 * skip link.
 */
function restore(to: HTMLElement | null): void {
  if (to?.isConnected === true) {
    to.focus?.({ preventScroll: true });
    return;
  }
  const below = topModalElement();
  const target =
    below === undefined ? document.getElementById('main') : (reachable(below)[0] ?? below);
  target?.focus?.({ preventScroll: true });
}

/**
 * Wire dialog focus behavior while `open()` is true. `el` returns the dialog
 * element (a ref); reads happen inside the handlers so the ref can be set
 * after mount. Call once from the dialog component's body.
 *
 * `initialFocus` (#6563) names the element the open lands on instead of the first
 * reachable control — a drawer's heading, or a confirm's first button. It is read
 * once, in the same microtask as the default, and an `undefined` answer falls back
 * to the default, so a target that is not rendered yet never strands focus outside.
 * The Tab trap, Escape and the restore on close do not read it.
 */
export function useDialogTrap(
  open: () => boolean,
  el: () => HTMLElement | undefined,
  onClose: () => void,
  initialFocus?: () => HTMLElement | undefined,
): void {
  createEffect(() => {
    if (!open()) return;
    const restoreTo = document.activeElement as HTMLElement | null;
    const self = pushModalLayer(el);
    // Focus the first control once the dialog is in the DOM.
    queueMicrotask(() => {
      // A fast re-close can fire onCleanup's focus-restore before this runs;
      // bail so the stale callback doesn't steal focus back.
      if (!open()) return;
      const dialog = el();
      const first = initialFocus?.() ?? (dialog === undefined ? undefined : reachable(dialog)[0]);
      // preventScroll: a slide-in dialog starts off-screen (transform), so a
      // scroll-into-view here fights the open animation and jumps the page.
      (first ?? dialog)?.focus({ preventScroll: true });
    });

    const onKeyDown = (event: KeyboardEvent): void => {
      // A control inside already handled the key (a combobox closing its
      // own list on Escape) — neither branch below is this trap's to take.
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        // Escape goes to the topmost layer of ANY kind: a Popover opened
        // over this dialog owns it instead — `isTopLayer` is what decides
        // that, not listener-registration order.
        if (!isTopLayer(self)) return;
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === 'Tab') {
        // Tab containment is a MODAL concern, not "topmost layer" — a
        // non-modal Popover stacked above this dialog must not switch its
        // Tab trap off just by being open (#5205 review, round 3).
        if (!isTopModal(self)) return;
        const dialog = el();
        if (dialog !== undefined) keepTabInside(dialog, event);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    onCleanup(() => {
      document.removeEventListener('keydown', onKeyDown);
      popLayer(self);
      restore(restoreTo);
    });
  });
}
